import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';

import JournalEntry from './JournalEntry';
import JournalEntryLoading from './JournalEntryLoading';
import loadingEntryData from '../utils';

import Spinner from '../../../shared/_TBComponents/Spinner';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import { ColorPrimary } from '../../../shared/GlobalStyles';

import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import { useJournalVariant } from '../JournalVariantContext';
import {
  fetchNextPageForAthlete,
  fetchNextPageForAthletes,
  fetchNextPageForGroup,
  fetchNextPageMyJournal,
} from '../ducks/journalActions';

const EndOfEntries = styled('div')`
  align-items: start;
  color: ${ColorPrimary};
  display: flex;
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 900;
  height: 60px;
  justify-content: center;
  letter-spacing: 1.3px;
  line-height: 16px;
  padding-bottom: 20px;
  text-transform: uppercase;
`;

const EndOfListRef = styled('div')`
  color: transparent;
  height: 24px;
`;

const JournalListContainer = styled('div')`
  padding-left: ${(props) => (props.isBuildVariant ? '5px' : '15px')};
  padding-right: ${(props) => (props.isBuildVariant ? '5px' : '15px')};
  position: relative;

  @media screen and (max-width: 640px) {
    min-width: 380px;
  }

  @media screen and (max-width: 576px) {
    min-width: 312px;
    padding-left: 0;
    padding-right: 0;
  }
`;

const NoEntriesContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-top: 50px;
`;

const NoEntriesText = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  margin-top: 10px;
`;

const JournalEntryList = () => {
  const activeTab = useSelector((state) => state.journal.data.activeTab);
  const currentAthlete = useSelector((state) => state.journal.data.currentAthlete);
  const currentPage = useSelector((state) => state.journal.data.page);
  const isEndOfList = useSelector((state) => state.journal.data.isEndOfList);
  const journalEntriesData = useSelector((state) => state.journal.data.journalEntriesData);
  const isEntriesLoading = useSelector((state) => state.journal.ui.isEntriesLoading);
  const nextPageLoading = useSelector((state) => state.journal.ui.nextPageLoading);
  const { accountCode, id: currentUserId } = useSelector((state) => state.auth.data.currentUser);
  const isBuildVariant = useJournalVariant() === 'build';
  const dispatch = useDispatch();
  const observer = useRef();

  const myJournal = currentAthlete?.id === currentUserId;
  const showNoEntries = Array.isArray(journalEntriesData)
    && !journalEntriesData.length
    && !isEntriesLoading;

  const lastListItemRef = useCallback(
    (nodeElement) => {
      if (nextPageLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !isEndOfList) {
          if (myJournal) {
            dispatch(fetchNextPageMyJournal(accountCode, currentPage));
            journalTracker('Main page', 'Fetching next page of entries - My Journal');
          } else if (activeTab === 'ALL_ATHLETES') {
            dispatch(fetchNextPageForAthletes(accountCode, currentPage));
            journalTracker('Main page', 'Fetching next page of entries - All Athletes');
          } else if (activeTab === 'SINGLE_ATHLETE') {
            dispatch(fetchNextPageForAthlete(accountCode, currentPage));
            journalTracker('Main page', 'Fetching next page of entries - Single Athlete');
          } else if (activeTab === 'GROUPS') {
            dispatch(fetchNextPageForGroup(accountCode, currentPage));
            journalTracker('Main page', 'Fetching next page of entries - Groups');
          }
        }
      });
      if (nodeElement) observer.current.observe(nodeElement);
    },
    [
      accountCode,
      activeTab,
      currentPage,
      dispatch,
      isEndOfList,
      myJournal,
      nextPageLoading,
    ],
  );

  const renderLoadingView = () => (
    <>
      {loadingEntryData.map((entry) => (
        <JournalEntryLoading key={entry.id} id={entry.id} journalEntry={entry} />
      ))}
      <EndOfListRef aria-hidden='true'>End of Entries</EndOfListRef>
    </>
  );

  const renderEntriesList = () => (
    <>
      {journalEntriesData.map((journalEntry, index) => (
        <React.Fragment key={journalEntry.id}>
          <JournalEntry id={journalEntry.id} journalEntry={journalEntry} />
          <EndOfListRef
            aria-hidden='true'
            ref={
              journalEntriesData.length === index + 1
                ? lastListItemRef
                : null
            }
          />
        </React.Fragment>
      ))}
      {nextPageLoading && <Spinner large={false} aria-label='Loading more journal entries...' />}
      {journalEntriesData.length > 0 && isEndOfList && !nextPageLoading && (
        <EndOfEntries>End of Entries</EndOfEntries>
      )}
    </>
  );

  const renderNoEntriesView = () => (
    <NoEntriesContainer>
      <IcomoonReact
        aria-hidden='true'
        iconSet={iconSet}
        size={100}
        icon='note'
        color={ColorPrimary}
      />
      <NoEntriesText>There are no entries available</NoEntriesText>
    </NoEntriesContainer>
  );

  const renderContent = () => {
    if (isEntriesLoading) {
      return renderLoadingView();
    }
    if (showNoEntries) {
      return renderNoEntriesView();
    }
    return renderEntriesList();
  };

  return (
    <JournalListContainer isBuildVariant={isBuildVariant}>
      {renderContent()}
    </JournalListContainer>
  );
};

export default JournalEntryList;
